import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/api'
import type { User, LoginRequest, RegisterRequest } from '@/types'
import { useWorkspaceStore } from './workspace'

export const useAuthStore = defineStore('auth', () => {
  // 从 localStorage 恢复用户信息
  const storedUser = localStorage.getItem('user_info')
  let initialUser: User | null = null
  if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
    try {
      initialUser = JSON.parse(storedUser)
    } catch (e) {
      // 如果解析失败，清除无效数据
      localStorage.removeItem('user_info')
    }
  }

  const user = ref<User | null>(initialUser)
  const accessToken = ref<string | null>(localStorage.getItem('access_token'))
  const refreshToken = ref<string | null>(localStorage.getItem('refresh_token'))

  const isAuthenticated = computed(() => !!accessToken.value)
  const userName = computed(() => user.value?.username || '')

  async function login(credentials: LoginRequest) {
    try {
      // 清理旧的workspace数据（可能是之前用户的数据）
      const workspaceStore = useWorkspaceStore()
      workspaceStore.clearWorkspaceData()

      const response = await api.login(credentials)
      accessToken.value = response.access_token
      refreshToken.value = response.refresh_token
      user.value = response.user

      localStorage.setItem('access_token', response.access_token)
      localStorage.setItem('refresh_token', response.refresh_token)
      localStorage.setItem('user_info', JSON.stringify(response.user))

      // 登录成功后，加载并选择默认workspace
      try {
        await workspaceStore.fetchWorkspaces(true) // forceSelectDefault = true
      } catch (error) {
        // 忽略workspace加载错误，让路由守卫或其他地方处理
        console.warn('Failed to load workspaces after login:', error)
      }

      return response
    } catch (error) {
      throw error
    }
  }

  async function register(userData: RegisterRequest) {
    try {
      const response = await api.register(userData)
      return response
    } catch (error) {
      throw error
    }
  }

  async function logout() {
    try {
      // 尝试调用后端登出接口（如果token无效也没关系）
      try {
        await api.logout()
      } catch (error) {
        // 忽略登出接口的错误（token可能已过期）
        console.warn('Logout API call failed:', error)
      }
    } finally {
      // 清理workspace数据
      const workspaceStore = useWorkspaceStore()
      workspaceStore.clearWorkspaceData()

      // 无论接口调用成功与否，都清理本地状态
      user.value = null
      accessToken.value = null
      refreshToken.value = null

      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user_info')
    }
  }

  return {
    user,
    accessToken,
    refreshToken,
    isAuthenticated,
    userName,
    login,
    register,
    logout
  }
})